import React, { Suspense, FC, ReactElement, useMemo, useState } from 'react';
import classnames from 'classnames';
import { ErrorBoundary, useRouter, useSignal } from '@funtech-int/rival-react';
import {
    AvailabilityAnnounce,
    NextEpisode,
    Progress,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import {
    useFeatureFlags,
    usePaymentPlace,
    type PaymentPlace,
    useAuthManager,
} from '@funtech-int/yango-shared';

import { MenuItem } from '@/shared/components/MenuItem/MenuItem';
import { Dropdown } from '@/shared/components/Dropdown/Dropdown';
import { checkIsTvSeries } from '@/shared/lib/movie';
import { Button } from '@/shared/components/Button/Button';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { MediaBillingFeature } from '@/shared/constants/billingFeatures';
import { useMovieUGC } from '@/shared/hooks/useMovieUGC';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import PlayIcon from '@/shared/assets/MoviePlay';
import { UnbookmarkIcon } from '@/shared/assets/Unbookmark';
import { BookmarkIcon } from '@/shared/assets/Bookmark';
import ForbiddenOn from '@/shared/assets/ForbiddenOn';
import Forbidden from '@/shared/assets/Forbidden';
import EyeOn from '@/shared/assets/EyeOn';
import Eye from '@/shared/assets/Eye';
import DotsHorizontal from '@/shared/assets/DotsHorizontal';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { Evaluation } from '@/shared/assets/Evaluation';
import { useMovieBuyButtonClickHandler } from '@/shared/hooks/useMovieBuyButtonClickHandler';
import { Menu } from '@/shared/components/Menu/Menu';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { useSendSuccessPurchaseEvent } from '@/shared/hooks/useSendSuccessPurchaseEvent';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { usePaymentWidgetModal } from '@/shared/hooks/usePopups';

import trailerIcon from '../../assets/trailer.svg';
import { contentActionsBuilder, ContentActionsConfig } from '../../lib/contentActionsBuilder';
import { useI18n } from '../../hooks/useI18n';
import { AnnounceContent } from '../AnnounceContent/AnnounceContent';
import { OfferContent } from '../OfferContent/OfferContent';
import { WatchingProgressContent } from '../WatchingProgressContent/WatchingProgressContent';
import { useMovieCardContentActions } from '../../hooks/useMovieCardContentActions';
import { getWatchingText } from '../../lib/watchingText';
import { Emotion, EmotionSelector } from '../EmotionSelector/EmotionSelector';
import { useEmotions } from '../../hooks/useEmotions';
import { ExploreButton } from '../ExploreButton/ExploreButton';
import { MovieCardContentActionsSkeleton } from '../MovieCardSkeleton/_MovieCardContentActionsSkeleton';
import { MovieContentActionsAnalytics } from '../../lib/movieCardAnalytics';
import { AutoStartMovie } from '../AutoStartMovie/AutoStartMovie';

import styles from './MovieCardContentActions.module.css';
import { FakeMovieTooltip } from './_FakeMovieTooltip';
import { MovieCardContentActionsError } from './_MovieCardContentActionsError';

interface MovieCardContentActionsComponentProps {
    contentActionsConfig: ContentActionsConfig;
    analytics: MovieContentActionsAnalytics;
    contentId: string;
    movieId: number;
    isTvSeries: boolean;
    announce: AvailabilityAnnounce | null;
    billingFeatureName: string;
    mainTrailerContentId: string | undefined;
    nextEpisode: NextEpisode | null;
    progress: Progress | null;
    disclaimer: string | undefined;
    paymentPlace?: PaymentPlace;
}

const LEAN_BILLING_FEATURES: string[] = [
    MediaBillingFeature.CRUNCHYROLL,
    MediaBillingFeature.AMEDIATEKA,
];

const BUTTON_SIZE = 52;

const MovieCardContentActionsComponent: FC<MovieCardContentActionsComponentProps> = ({
    contentId,
    movieId,
    mainTrailerContentId,
    contentActionsConfig,
    isTvSeries,
    announce,
    billingFeatureName,
    progress,
    nextEpisode,
    disclaimer,
    analytics,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const hasSubscription = useHasSubscription();
    const { isAuthorized } = useAuthManager();
    const loginUrl = useLoginUrl();
    const movieUGC = useMovieUGC(movieId);
    const emotion = useSignal(movieUGC.emotion);
    const plannedToWatch = useSignal(movieUGC.plannedToWatch);
    const notInterested = useSignal(movieUGC.notInterested);
    const watched = useSignal(movieUGC.watched);
    const router = useRouter();
    const { flags } = useFeatureFlags();
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled', 'boolean', false);
    const emotionsController = useEmotions();
    const currentEmotion = emotionsController.getCurrentEmotion(emotion ?? null);
    const [isEmotionSelectorOpen, setIsEmotionSelectorOpen] = useState(false);
    const movieBuyButtonClickHandler = useMovieBuyButtonClickHandler();
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const offer = paymentPlace?.offers?.[0];
    const sendSuccessPurchaseMetrikaEvent = useSendSuccessPurchaseEvent(offer);

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));

    const paymentWidget = usePaymentWidgetModal();

    const controller = useMemo(() => {
        function playContent() {
            if (isTvSeries && nextEpisode?.contentId) {
                startPlaying({
                    router,
                    seriesInfo: {
                        seasonNumber: nextEpisode.seasonNumber,
                        episodeNumber: nextEpisode.episodeNumber,
                    },
                    contentId: nextEpisode.contentId,
                    contentType: PlayerContentType.EPISODE,
                    isNewPlayerEnabled,
                });
                return;
            }

            startPlaying({
                router,
                contentId,
                contentType: PlayerContentType.FILM,
                isNewPlayerEnabled,
            });
        }

        function onPurchaseSuccess() {
            subscriptionRefetch();
            sendSuccessPurchaseMetrikaEvent();

            if (offer) {
                analytics?.offerSucceed(offer);
            }
        }

        return {
            onOfferShow: () => {
                if (offer) {
                    paymentPlace?.analytics.offerShowed(offer);
                    analytics?.offerShowed(offer);
                }
            },
            onOfferClick: () => {
                analytics?.movieImpressionNavigated('payment');
                if (!offer) return;

                paymentPlace?.analytics.offerClicked(offer);
                analytics?.offerNavigated(offer);
                movieBuyButtonClickHandler();

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                    onSuccessClose: playContent,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
            onWatchClick: () => {
                analytics.movieImpressionNavigated('player');
                playContent();
            },
            onPlannedToWatchClick: () => {
                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }
                analytics?.movieImpressionMarked(
                    movieUGC.plannedToWatch.get() ? 'unbookmarked' : 'bookmarked'
                );
                movieUGC.togglePlannedToWatch();
            },
            onNotInterestedClick: () => {
                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }
                analytics.movieImpressionMarked(
                    movieUGC.notInterested.get()
                        ? 'unmarkedAsNotInteresting'
                        : 'markedAsNotInteresting'
                );
                movieUGC.toggleNotInterested();
            },
            onWatchedClick: () => {
                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }
                analytics.movieImpressionMarked(movieUGC.watched.get() ? 'unwatched' : 'watched');
                movieUGC.toggleWatched();
            },
            onWatchTrailer: () => {
                if (!mainTrailerContentId) {
                    return;
                }
                analytics.movieImpressionNavigated('trailer_player');
                startPlaying({ router, contentId: mainTrailerContentId, isNewPlayerEnabled });
            },
            openEmotions: () => {
                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }
                setIsEmotionSelectorOpen(true);
            },
            handleEmotionClick: (emotion: Emotion) => {
                movieUGC.setEmotion(emotion.value);
                setIsEmotionSelectorOpen(false);
            },
            handleEmotionSelectorClose: () => {
                setIsEmotionSelectorOpen(false);
            },
        };
    }, [
        isTvSeries,
        nextEpisode?.contentId,
        nextEpisode?.seasonNumber,
        nextEpisode?.episodeNumber,
        router,
        contentId,
        isNewPlayerEnabled,
        offer,
        paymentWidget,
        paymentPlace,
        analytics,
        movieBuyButtonClickHandler,
        isAuthorized,
        movieUGC,
        loginUrl,
        mainTrailerContentId,
        subscriptionRefetch,
        sendSuccessPurchaseMetrikaEvent,
        offerLoginUrl,
    ]);

    let content: ReactElement | undefined;
    let note: ReactElement | undefined;

    if (contentActionsConfig.content?.type === 'offer' && offer) {
        content = <OfferContent billingFeatureName={billingFeatureName} offer={offer} />;
    } else if (contentActionsConfig.content?.type === 'announce') {
        content = (
            <AnnounceContent
                announcePromise={announce?.announcePromise ?? null}
                availabilityDate={announce?.availabilityDate ?? null}
            />
        );
    } else if (
        contentActionsConfig.content?.type === 'nextEpisode' ||
        contentActionsConfig.content?.type === 'progress'
    ) {
        content = <WatchingProgressContent nextEpisode={nextEpisode} movieProgress={progress} />;
    } else if (contentActionsConfig.content?.type === 'discovery') {
        content = <FakeMovieTooltip />;
    }

    if (contentActionsConfig.note?.type === 'disclaimer') {
        note = <div className={styles.note}>{disclaimer}</div>;
    } else if (
        contentActionsConfig.note?.type === 'subscription' &&
        offer &&
        !hasSubscription &&
        LEAN_BILLING_FEATURES.some((billingFeature) => billingFeature === billingFeatureName)
    ) {
        note = <div className={styles.note}>{offer.offerTexts.subscriptionName}</div>;
    }

    return (
        <div>
            {content}

            <div className={styles.actionsWrapper}>
                {isEmotionSelectorOpen ? (
                    <EmotionSelector
                        buttonType="primary"
                        activeValue={currentEmotion?.value}
                        emotions={Object.values(emotionsController.emotions)}
                        size={BUTTON_SIZE}
                        colorScheme="dark"
                        onClick={controller.handleEmotionClick}
                        onCancelClick={controller.handleEmotionSelectorClose}
                    />
                ) : (
                    <>
                        <div className={styles.actions}>
                            {contentActionsConfig.actions.map((action) => {
                                if (action.type === 'watch') {
                                    const watchingText = getWatchingText({
                                        isTvSeries,
                                        i18n,
                                        progress,
                                        nextEpisode,
                                    });

                                    return (
                                        <Button
                                            key="watch"
                                            name="Watch"
                                            variant="accent"
                                            size={BUTTON_SIZE}
                                            startIcon={<PlayIcon fill="#fff" />}
                                            className={classnames(styles.button, styles.watch)}
                                            data-test-id="MainButton_watch"
                                            onClick={controller.onWatchClick}
                                        >
                                            {watchingText}
                                        </Button>
                                    );
                                }

                                if (action.type === 'offer') {
                                    if (!offer) {
                                        return null;
                                    }

                                    return (
                                        <Button
                                            key="offer"
                                            size={BUTTON_SIZE}
                                            variant={!isAuthorized ? 'accent' : 'plus'}
                                            startIcon={<PlayIcon fill="#fff" />}
                                            onClick={controller.onOfferClick}
                                            className={classnames(styles.button, styles.offer)}
                                            name="Offer"
                                            data-test-id="MovieOfferButton"
                                            onShow={controller.onOfferShow}
                                        >
                                            {isTvSeries
                                                ? i18n('content_actions__play_series_text')
                                                : i18n('content_actions__play_movie_text')}
                                        </Button>
                                    );
                                }

                                if (action.type === 'plannedToWatch') {
                                    const plannedToWatchText = i18n(
                                        'planned_to_watch__default_text'
                                    );

                                    return (
                                        <Button
                                            key="bookmark"
                                            name="Bookmark"
                                            variant={announce ? 'accentWhite' : 'primary'}
                                            size={BUTTON_SIZE}
                                            theme="dark"
                                            startIcon={
                                                plannedToWatch ? (
                                                    <UnbookmarkIcon fill="var(--primary-color)" />
                                                ) : (
                                                    <BookmarkIcon
                                                        fill={announce ? '#000' : '#FFF'}
                                                    />
                                                )
                                            }
                                            className={styles.button}
                                            aria-label={plannedToWatchText}
                                            aria-pressed={plannedToWatch}
                                            data-test-id="ContentActions_bookmarkButton"
                                            onClick={controller.onPlannedToWatchClick}
                                        >
                                            {plannedToWatchText}
                                        </Button>
                                    );
                                }

                                if (action.type === 'trailer') {
                                    return (
                                        <Button
                                            key="trailer"
                                            name="Trailer"
                                            onClick={controller.onWatchTrailer}
                                            size={BUTTON_SIZE}
                                            className={classnames(
                                                styles.button,
                                                styles.trailerButton
                                            )}
                                            theme="dark"
                                            variant="primary"
                                            startIcon={
                                                <img
                                                    alt={i18n('content_actions__trailer_text')}
                                                    role="presentation"
                                                    src={trailerIcon}
                                                    className={styles.icon}
                                                    data-tid="Subtitles"
                                                />
                                            }
                                            data-test-id="ContentActions_trailer"
                                        >
                                            {i18n('content_actions__trailer_text')}
                                        </Button>
                                    );
                                }

                                if (action.type === 'emotions' && isAuthorized) {
                                    return (
                                        <Button
                                            key="emotions"
                                            name="Emotions"
                                            variant="primary"
                                            size={BUTTON_SIZE}
                                            className={styles.button}
                                            theme="dark"
                                            startIcon={
                                                currentEmotion ? (
                                                    <img
                                                        src={currentEmotion.icon}
                                                        alt=""
                                                        width={24}
                                                        height={24}
                                                    />
                                                ) : (
                                                    <Evaluation fill="#fff" />
                                                )
                                            }
                                            onClick={controller.openEmotions}
                                            data-test-id="ContentActions_emotions"
                                        />
                                    );
                                }

                                if (
                                    action.type === 'notInteresting' &&
                                    contentActionsConfig.content?.type === 'announce'
                                ) {
                                    const notInterestedText = i18n(
                                        'content_actions__not_interested_text'
                                    );

                                    return (
                                        <Button
                                            className={styles.button}
                                            key="notInterested"
                                            name="Not interested"
                                            variant="primary"
                                            size={BUTTON_SIZE}
                                            theme="dark"
                                            startIcon={
                                                notInterested ? (
                                                    <ForbiddenOn fill="var(--primary-color)" />
                                                ) : (
                                                    <Forbidden fill="#FFF" />
                                                )
                                            }
                                            aria-label={notInterestedText}
                                            aria-pressed={notInterested}
                                            data-test-id="ContentActions_notInterestedButton"
                                            onClick={controller.onNotInterestedClick}
                                        />
                                    );
                                }

                                if (action.type === 'dropdown') {
                                    const ForbiddenIcon = notInterested ? ForbiddenOn : Forbidden;
                                    const EyeIcon = watched ? EyeOn : Eye;

                                    return (
                                        <Dropdown<HTMLButtonElement>
                                            key="dropdown"
                                            renderDropdownTrigger={({
                                                onClick,
                                                ref,
                                                'aria-expanded': ariaExpanded,
                                                'aria-controls': ariaControls,
                                            }) => (
                                                <Button
                                                    id="moreActionsBtn"
                                                    ref={ref}
                                                    name="MoreActions"
                                                    onClick={onClick}
                                                    size={BUTTON_SIZE}
                                                    className={classnames(
                                                        styles.button,
                                                        styles.moreActionsButton
                                                    )}
                                                    theme="dark"
                                                    startIcon={<DotsHorizontal fill="#FFF" />}
                                                    variant="primary"
                                                    aria-label={i18n(
                                                        'content_actions__more_actions_text'
                                                    )}
                                                    data-test-id="ContentActions_moreActionsButton"
                                                    aria-expanded={ariaExpanded}
                                                    aria-controls={ariaControls}
                                                />
                                            )}
                                            content={
                                                <Menu>
                                                    <MenuItem
                                                        text={i18n(
                                                            'content_actions__not_interested_text'
                                                        )}
                                                        icon={
                                                            <ForbiddenIcon
                                                                fill={
                                                                    notInterested
                                                                        ? 'var(--primary-color)'
                                                                        : '#FFF'
                                                                }
                                                            />
                                                        }
                                                        pressed={notInterested}
                                                        onClick={controller.onNotInterestedClick}
                                                    />
                                                    <MenuItem
                                                        text={i18n('content_actions__watched_text')}
                                                        icon={
                                                            <EyeIcon
                                                                fill={
                                                                    watched
                                                                        ? 'var(--primary-color)'
                                                                        : '#FFF'
                                                                }
                                                            />
                                                        }
                                                        pressed={watched}
                                                        onClick={controller.onWatchedClick}
                                                    />
                                                </Menu>
                                            }
                                        />
                                    );
                                }

                                if (action.type === 'discovery') {
                                    return (
                                        <ExploreButton key="discovery" className={styles.button} />
                                    );
                                }

                                return null;
                            })}
                        </div>
                        {note}
                    </>
                )}
            </div>
        </div>
    );
};

const MovieCardContentActionsOffer: FC<MovieCardContentActionsComponentProps> = (props) => {
    const { billingFeatureName } = props;

    const page = usePaymentPage();

    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.WatchButton],
        extraParams: {
            customProperties: { billingFeatureName },
        },
    });

    return <MovieCardContentActionsComponent {...props} paymentPlace={paymentPlace} />;
};

interface MovieCardContentActionsProps {
    contentId: string;
    analytics: MovieContentActionsAnalytics;
}

const MovieCardContentActionsContainer: FC<MovieCardContentActionsProps> = ({
    contentId,
    analytics,
}) => {
    const { contentActions, watchingProgress } = useMovieCardContentActions({ contentId });

    const contentActionsConfig = useMemo(() => {
        const builder = contentActionsBuilder()
            .withViewOption(contentActions?.viewOption)
            .withAnnounce(contentActions?.announce)
            .withNextEpisode(watchingProgress?.nextEpisode)
            .withProgress(watchingProgress?.movieProgress)
            .withTrailer(contentActions?.mainTrailer?.trailerContentId)
            .withNotInteresting()
            .withDropdown()
            .withEmotions();

        return builder.build();
    }, [
        contentActions?.announce,
        contentActions?.mainTrailer?.trailerContentId,
        contentActions?.viewOption,
        watchingProgress?.movieProgress,
        watchingProgress?.nextEpisode,
    ]);

    if (!contentActions) {
        return null;
    }

    const ContentActionsComponent =
        contentActions.viewOption?.isPurchasable &&
        contentActions.viewOption?.isWatchable &&
        !contentActions.announce
            ? MovieCardContentActionsOffer
            : MovieCardContentActionsComponent;

    return (
        <>
            <ContentActionsComponent
                contentActionsConfig={contentActionsConfig}
                contentId={contentId}
                movieId={contentActions.id}
                isTvSeries={checkIsTvSeries(contentActions.typename)}
                announce={contentActions.announce}
                billingFeatureName={
                    (contentActions.viewOption?.purchaseOptionCustomProperties
                        ?.billingFeatureName as string) ?? ''
                }
                progress={watchingProgress?.movieProgress ?? null}
                mainTrailerContentId={contentActions.mainTrailer?.trailerContentId}
                nextEpisode={watchingProgress?.nextEpisode ?? null}
                disclaimer={contentActions.viewOption?.disclaimer}
                analytics={analytics}
            />
            <AutoStartMovie
                isMoviePurchased={Boolean(contentActions.viewOption?.isPurchased)}
                filmId={contentId}
                nextEpisode={watchingProgress?.nextEpisode ?? null}
            />
        </>
    );
};

export const MovieCardContentActions: FC<MovieCardContentActionsProps> = ({
    contentId,
    analytics,
}) => (
    <ErrorBoundary fallback={MovieCardContentActionsError}>
        <Suspense fallback={<MovieCardContentActionsSkeleton />}>
            <MovieCardContentActionsContainer contentId={contentId} analytics={analytics} />
        </Suspense>
    </ErrorBoundary>
);
